import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X, MapPin, HandCoins, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { BRAND, BRANCHES, telLink } from "@/lib/brand";
import { TESTIDS } from "@/constants/testIds";

const links = [
  { to: "/", label: "Home", testid: TESTIDS.navLinkHome },
  { to: "/services", label: "Services", testid: TESTIDS.navLinkServices },
  {
    to: "/sell-gold-gurgaon",
    label: "Sell Gold",
    testid: TESTIDS.navLinkSellGold,
  },
  {
    to: "/sell-silver-gurgaon",
    label: "Sell Silver",
    testid: TESTIDS.navLinkSellSilver,
  },
  {
    to: "/cash-for-gold-golf-course-road-gurgaon",
    label: "Golf Course Rd",
    testid: TESTIDS.navLinkGCR,
  },
  {
    to: "/gold-loan-vs-selling-gold",
    label: "Loan or Sell?",
    testid: TESTIDS.navLinkLoanVsSell,
  },
  {
    to: "/mortgaged-gold",
    label: "Girvi Sona",
    testid: TESTIDS.navLinkMortgage,
    highlight: true,
    children: [
      {
        to: "/mortgaged-gold",
        label: "Girvi Sona Buyback",
        testid: "nav-link-girvi-buyback",
      },
      {
        to: "/bank-mortgaged-jewellery",
        label: "Bank Mortgaged Jewellery",
        testid: "nav-link-bank-jewellery",
      },
    ],
  },
  { to: "/gallery", label: "Gallery", testid: "nav-link-gallery" },
  { to: "/branches", label: "Branches", testid: TESTIDS.navLinkBranches },
  { to: "/contact", label: "Contact", testid: TESTIDS.navLinkContact },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setOpen(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={TESTIDS.nav}
      className="sticky top-0 z-40 bg-[#FFFDF8]/95 backdrop-blur-xl border-b border-[#D4AF37]/25"
    >
      {/* Auspicious 'Shubh Labh' — traditional Indian shop opening (centered top) */}
      <div
        data-testid="shubh-labh-banner"
        aria-hidden="true"
        className="w-full flex items-center justify-center bg-[#FFFDF8] pt-2 pb-1 border-b border-[#E5DCC5]/50"
      >
        <span
          className="inline-flex items-center gap-3 text-[#0F7A3B] text-xs md:text-sm font-bold uppercase tracking-[0.4em] select-none"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          <span className="text-[#D4AF37]">✦</span>
          Shubh &nbsp;·&nbsp; Labh
          <span className="text-[#D4AF37]">✦</span>
        </span>
      </div>

      {/* Top strip — brand + branch locations at top right */}
      <div className="border-b border-[#E5DCC5]/70 bg-[#FFFDF8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 md:py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-8">
            {/* Brand — bigger + prominent with hand-getting-cash logo */}
            <Link
              data-testid={TESTIDS.navBrand}
              to="/"
              className="flex items-center gap-4 group"
            >
              <span
                data-testid="brand-logo-mark"
                aria-label="Hand receiving cash — cash for gold logo"
                className="relative inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 shrink-0 bg-[#641C24] text-[#D4AF37] shadow-[0_2px_0_0_#D4AF37]"
              >
                <HandCoins
                  size={30}
                  strokeWidth={1.6}
                  className="md:hidden"
                />
                <HandCoins
                  size={36}
                  strokeWidth={1.6}
                  className="hidden md:block"
                />
              </span>
              <span className="flex items-baseline gap-3">
                <span className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#641C24] leading-none">
                  Top<span className="text-[#D4AF37]">Rate</span>
                </span>
                <span className="hidden sm:inline text-[10px] uppercase tracking-[0.3em] text-[#4A3B3C] pb-1">
                  Cash for Gold · Sona Do Paisa Lo
                </span>
              </span>
            </Link>

            {/* Branch locations — top right */}
            <div className="hidden md:flex items-start gap-6 shrink-0">
              {BRANCHES.map((b) => (
                <div
                  key={b.id}
                  data-testid={`nav-branch-${b.id}`}
                  className="flex items-start gap-2 max-w-[13rem]"
                >
                  <MapPin
                    size={15}
                    className="text-[#D4AF37] mt-0.5 shrink-0"
                    strokeWidth={1.8}
                  />
                  <div className="text-[11px] leading-tight">
                    <div className="uppercase tracking-[0.16em] font-bold text-[#641C24]">
                      {b.name.replace(" Branch", "")}
                    </div>
                    <div className="text-[#4A3B3C] mt-0.5">{b.address}</div>
                  </div>
                </div>
              ))}
              <a
                data-testid={TESTIDS.navCallCta}
                href={telLink}
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#2D181A] hover:bg-[#B8860B] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors self-start shrink-0"
              >
                <Phone size={13} strokeWidth={2.2} />
                {BRAND.phone}
              </a>
            </div>

            {/* Mobile: compact branch strip */}
            <div className="md:hidden flex flex-col gap-2 text-[11px] text-[#4A3B3C] border-l-2 border-[#D4AF37] pl-3">
              {BRANCHES.map((b) => (
                <div key={b.id} className="leading-tight">
                  <span className="uppercase tracking-wider font-bold text-[#641C24]">
                    {b.name.replace(" Branch", "")}
                  </span>{" "}
                  · {b.address}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main nav links row */}
      <nav className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-14">
          <div className="hidden md:flex items-center gap-5 flex-wrap">
            {links.map((l) =>
              l.children ? (
                <div key={l.to} className="relative group">
                  <NavLink
                    to={l.to}
                    data-testid={l.testid}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.18em] uppercase transition-colors whitespace-nowrap ${
                        isActive
                          ? "text-[#641C24]"
                          : "text-[#B8860B] hover:text-[#641C24]"
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown size={12} strokeWidth={2.4} />
                  </NavLink>
                  <div className="absolute left-0 top-full pt-3 hidden group-hover:block z-50">
                    <div className="bg-[#FFFDF8] border border-[#E5DCC5] shadow-xl min-w-[15rem]">
                      {l.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={c.testid}
                          className={({ isActive }) =>
                            `block px-5 py-3.5 text-[10px] font-semibold tracking-[0.18em] uppercase border-b border-[#E5DCC5] last:border-b-0 transition-colors ${
                              isActive
                                ? "text-[#641C24] bg-[#FAF6EC]"
                                : "text-[#2D181A]/75 hover:text-[#641C24] hover:bg-[#FAF6EC]"
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `text-[10px] font-semibold tracking-[0.18em] uppercase transition-colors whitespace-nowrap ${
                      isActive
                        ? "text-[#641C24]"
                        : l.highlight
                        ? "text-[#B8860B] hover:text-[#641C24]"
                        : "text-[#2D181A]/75 hover:text-[#641C24]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
          </div>

          <a
            href={telLink}
            className="hidden xl:inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#641C24] hover:text-[#B8860B] whitespace-nowrap shrink-0 ml-4"
          >
            <Phone size={13} /> Best rate
          </a>

          {/* Mobile trigger */}
          <div className="md:hidden flex items-center justify-between w-full">
            <a
              href={telLink}
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#2D181A] px-3 py-2 text-[11px] font-semibold uppercase tracking-wider"
            >
              <Phone size={13} /> Call
            </a>
            <button
              data-testid={TESTIDS.navMobileToggle}
              onClick={() => setOpen((s) => !s)}
              className="p-2 text-[#2D181A]"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden pb-5 pt-1 flex flex-col gap-4 border-t border-[#E5DCC5]/70 mt-1">
            {links.map((l) =>
              l.children ? (
                <div key={l.to} className="flex flex-col gap-3">
                  <span className="text-sm uppercase tracking-[0.2em] text-[#B8860B] font-semibold">
                    {l.label}
                  </span>
                  {l.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      data-testid={`${c.testid}-mobile`}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `pl-4 border-l-2 border-[#D4AF37] text-sm uppercase tracking-[0.2em] ${
                          isActive
                            ? "text-[#641C24] font-semibold"
                            : "text-[#2D181A]/80"
                        }`
                      }
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-sm uppercase tracking-[0.2em] ${
                      isActive
                        ? "text-[#641C24] font-semibold"
                        : "text-[#2D181A]/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
